import {Flex, Grid, GridItem, Text} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import React, {useMemo} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import Chip from '../../../components/chip'
import {useSortUrls} from '../../../hooks'
import SelectedRefinements from './selected-refinements'
import Sort from './sort'
import FilterSortButton from '../../../components/filter-sort/filter-sort-button'

const DesktopRefinementButton = ({refinement, selected, onOpen}) => {
    const count = useMemo(() => selected?.split('|').length, [selected])

    return (
        <Chip
            size="md"
            name={`${refinement.label} ${count ? `(${count})` : ''}`}
            orderable
            onChange={onOpen}
        />
    )
}

DesktopRefinementButton.propTypes = {
    refinement: PropTypes.object,
    selected: PropTypes.string,
    onOpen: PropTypes.func
}

const FiltersSection = ({
    basePath,
    hasSelectedRefinements,
    productSearchResult,
    resetLabel,
    onOpen,
    onResetFilters,
    onToggleFilter,
    searchQuery
}) => {
    const {formatMessage} = useIntl()
    const {sortingOptions} = productSearchResult || {}
    const sortUrls = useSortUrls({options: sortingOptions})

    const selectedRefinementsCount = useMemo(() => {
        // filter out cgid from selected refinements
        const {cgid, ...selectedRefinements} = productSearchResult?.selectedRefinements || {}
        return selectedRefinements
            ? Object.values(selectedRefinements).reduce(
                  (acc, curr) => acc + curr.split('|').length,
                  0
              )
            : 0
    }, [productSearchResult?.selectedRefinements])

    const desktopRefinements = useMemo(
        () => productSearchResult?.refinements?.filter((r) => r?.values?.length > 0) || [],
        [productSearchResult?.refinements]
    )

    const sortColumn = (
        <>
            <Sort
                display={{base: 'none', lg: 'block'}}
                sortUrls={sortUrls}
                productSearchResult={productSearchResult}
                basePath={basePath}
                searchQuery={searchQuery}
            />
            {!searchQuery && (
                <Text textStyle="Regular Small" color="stokkeGray.accessibleText">
                    {formatMessage(
                        {
                            id: 'product_list.total_text',
                            defaultMessage:
                                '{total} {total, plural, one {product} other {products}}'
                        },
                        {total: productSearchResult?.total}
                    )}
                </Text>
            )}
        </>
    )

    const selectedRefinements = Object.assign(
        {},
        ...Object.entries(productSearchResult?.selectedRefinements || {}).map(([key, value]) => {
            const filters = value.split('|')
            const labels = []

            filters.map((filter) => {
                const label = productSearchResult?.refinements
                    ?.find(({attributeId}) => attributeId === key)
                    ?.values?.find(({value}) => value === filter)?.label
                if (label) {
                    labels.push(label)
                }
            })

            // if (labels.length === 0 && key !== 'price') return null

            return {
                [key]: {
                    value,
                    label: labels.join('|') || value
                }
            }
        })
    )

    return (
        <Grid
            className="sf-listing-filter"
            gridTemplateColumns={{
                base: 'minmax(auto, 1fr) auto',
                lg: 'auto minmax(auto, 1fr) auto'
            }}
            gridTemplateRows="auto auto"
            gridGap={4}
            paddingX={{base: 4, lg: 8}}
            paddingTop={6}
            paddingBottom={{base: 8, lg: 6}}
        >
            {/* duplicate sort column to take up equal space from both sides */}
            <GridItem
                gridColumn="1"
                gridRow="1/-1"
                display={{base: 'none', lg: 'block'}}
                visibility="hidden"
            >
                {sortColumn}
            </GridItem>
            <GridItem
                gridColumn={{base: '1', lg: '2'}}
                display="flex"
                justifyContent={{base: 'flex-start', lg: 'center'}}
            >
                {/* mobile button */}
                <FilterSortButton
                    chipProps={{display: {base: 'flex', lg: 'none'}}}
                    count={selectedRefinementsCount}
                    onClick={onOpen}
                />
                {/* desktop refinements */}
                {desktopRefinements.length > 0 && (
                    <Flex display={{base: 'none', lg: 'flex'}} align="center" gap={3}>
                        <Text color="stokkeGray.accessibleText" textStyle="Regular Small">
                            <FormattedMessage
                                defaultMessage="Filter"
                                id="product_list.label.filter"
                            />
                        </Text>
                        {desktopRefinements.map((refinement, idx) => (
                            <DesktopRefinementButton
                                key={idx}
                                refinement={refinement}
                                selected={
                                    productSearchResult?.selectedRefinements?.[
                                        refinement.attributeId
                                    ]
                                }
                                onOpen={onOpen}
                            />
                        ))}
                    </Flex>
                )}
            </GridItem>
            <GridItem
                gridColumn={{base: '2', lg: '3'}}
                gridRow={{base: '1', lg: '1/-1'}}
                textAlign="right"
                alignSelf={{base: 'center', lg: 'flex-start'}}
            >
                {sortColumn}
            </GridItem>

            <GridItem gridColumn={{base: '1/-1', lg: '2'}} gridRow="2">
                <SelectedRefinements
                    hasSelectedRefinements={hasSelectedRefinements}
                    resetFilters={onResetFilters}
                    resetLabel={resetLabel}
                    selectedFilterValues={selectedRefinements}
                    toggleFilter={onToggleFilter}
                />
            </GridItem>
        </Grid>
    )
}

FiltersSection.propTypes = {
    basePath: PropTypes.string,
    hasSelectedRefinements: PropTypes.bool,
    productSearchResult: PropTypes.object,
    onOpen: PropTypes.func,
    onResetFilters: PropTypes.func,
    onToggleFilter: PropTypes.func,
    searchQuery: PropTypes.string,
    resetLabel: PropTypes.string
}

export default FiltersSection
